import React, { useEffect, useRef } from "react";
import QrScanner from "qr-scanner";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import i18n from "i18next";
import { Box } from "@mui/material";
import { parseError } from "src/errorHandling/errorParsing.ts";
import { useDebounce } from "src/utils/useDebounce.ts";

const MULTIPLE_READ_COOLDOWN_MS = 1500;

export interface QrcodeReaderProps {
	onQrCodeRead: (qrCodeContent: string) => void | Promise<void>;
	allowMultipleReads?: boolean;
}

export const QrcodeReader = React.forwardRef(
	({ onQrCodeRead, allowMultipleReads }: QrcodeReaderProps, ref: React.ForwardedRef<HTMLDivElement | null>) => {
		const videoElRef = useRef<HTMLVideoElement>(null);
		const { logErrorAndShowOnDialog } = useErrorDialog();

		const codeHasBeenRead = useRef<boolean>(false);
		const debounceRead = useDebounce();

		useEffect(() => {
			if (!videoElRef.current) return;

			const qrScanner = new QrScanner(
				videoElRef.current,
				(result) => {
					if (codeHasBeenRead.current) return;

					codeHasBeenRead.current = true;
					debounceRead(10, async () => {
						await onQrCodeRead(result.data);
						if (allowMultipleReads) {
							setTimeout(() => {
								codeHasBeenRead.current = false;
							}, MULTIPLE_READ_COOLDOWN_MS);
						}
					});
				},
				{
					preferredCamera: "environment",
					highlightCodeOutline: true,
					highlightScanRegion: true,
				},
			);

			qrScanner.start().catch((error) => {
				logErrorAndShowOnDialog(i18n.t("failed_to_open_qr_code_reader"), parseError(error).message);
			});

			return () => {
				qrScanner.stop();
				qrScanner.destroy();
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<Box
				ref={ref}
				sx={{
					display: "flex",
					flex: 1,
					alignSelf: "stretch",
					margin: "30px",
				}}
			>
				<video
					style={{
						width: "100%",
						height: "100%",
					}}
					ref={videoElRef}
				/>
			</Box>
		);
	},
);
